import * as React from 'react'
import Head from 'next/head'
import { AppProps, AppContext } from 'next/app'
import getConfig from 'next/config'
import { withApollo } from 'request/apollo/withApollo'
import { onRefreshIdToken, getUser } from 'models/Auth'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { CssBaseline } from '@material-ui/core'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import dayjsUtils from '@date-io/dayjs'
import { appWithTranslation } from 'i18n'
import { createTheme } from 'components/themes'
import { ApolloClient } from '@apollo/client'
import Router, { useRouter } from 'next/router'
import analytics from 'utils/analytics'
import { UserInfo } from 'firebase'
import { UserProvider } from 'utils/context/UserContext'
import { SplitFactory } from '@splitsoftware/splitio-react'
import { Sentry, captureException } from 'utils/sentry'
import { getStandaloneName } from 'utils/standalone'
import { getTitle } from 'routes/titles'
import { MessageProvider } from 'components/Message/useMessage'
import { AnalyticsProvider } from 'utils/analytics'
import { ErrorBoundary } from '@sentry/react'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

const {
  publicRuntimeConfig: { splitioAuthorizeKey },
} = getConfig()

const description =
  'ユートニックはライブ動画から楽譜まで、あらゆる作品をデジタルカードとして作ったり、集めたりできるサービスです。'
const image = 'https://www.utoniq.com/static/images/ogp/ogp_logo.png'
const url = 'https://www.utoniq.com'

type Props = {
  apollo: ApolloClient<{}>
  user: UserInfo | null
  statusCode?: number
  artistName: string | null
  hasError?: boolean
  errorEventId?: string
} & AppProps

const UtoniqApp = ({ Component, pageProps, user, artistName }: Props) => {
  const router = useRouter()
  const theme = React.useMemo(() => createTheme(artistName), [artistName])
  const title = React.useMemo(() => getTitle(router.route, artistName), [
    router.route,
    artistName,
  ])

  React.useEffect(() => {
    analytics.page()

    Router.events.on('routeChangeComplete', () => {
      analytics.page()
    })

    onRefreshIdToken()

    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side')
    if (jssStyles && jssStyles.parentNode)
      jssStyles.parentNode.removeChild(jssStyles)
  }, [])

  return (
    <ErrorBoundary>
      <UserProvider value={{ currentUser: user, reloadUser: () => {} }}>
        <MessageProvider>
          <AnalyticsProvider>
            <MuiPickersUtilsProvider utils={dayjsUtils}>
              <StyledThemeProvider theme={theme}>
                <MuiThemeProvider theme={theme}>
                  <CssBaseline />
                  <Head>
                    <style>{`
                    html {
                      min-height:100%;/* make sure it is at least as tall as the viewport */
                      position:relative;
                      background:white;
                    }
                    body { 
                      margin: 0;
                      height:100%;
                      background: #f0f0f0;
                      font: 14px helvetica, arial, Hiragino Kaku Gothic ProN,ヒラギノ角ゴ Pro W3,メイリオ,Meiryo,ＭＳ Ｐゴシック,MS PGothic, sans-serif;
                      color: #263238;
                    }
                    h1 {
                      font-size: 28px;
                      margin-block-start: 5px;
                      margin-block-end: 5px;
                      font-weight: bold;
                    }
                    button {
                      background-color: transparent;
                      border: none;
                      cursor: pointer;
                      outline: none;
                      padding: 0;
                      appearance: none;
                    }
                    *:focus {
                      outline: 0
                    }
                    .fade-appear {
                      opacity: 0;
                    }
                    .fade-appear-done {
                      opacity: 1;
                      transition: opacity 300ms;
                    }
                  `}</style>
                    <meta
                      name="theme-color"
                      content={theme.palette.primary.main}
                    />
                    <meta
                      key="description"
                      itemProp="description"
                      name="description"
                      content={description}
                    />
                    <meta
                      data-n-head="true"
                      name="viewport"
                      content="minimum-scale=1, width=device-width, initial-scale=1, shrink-to-fit=no"
                    />
                    <meta key="name" itemProp="name" content={title} />
                    <meta key="image" itemProp="image" content={image} />

                    <meta key="og:url" property="og:url" content={url} />
                    <meta key="og:type" property="og:type" content="website" />
                    <meta key="og:title" property="og:title" content={title} />
                    <meta
                      key="og:description"
                      property="og:description"
                      content={description}
                    />
                    <meta key="og:image" property="og:image" content={image} />

                    <meta
                      key="twitter:card"
                      name="twitter:card"
                      content="summary_large_image"
                    />
                    <meta
                      key="twitter:title"
                      name="twitter:title"
                      content={title}
                    />
                    <meta
                      key="twitter:description"
                      name="twitter:description"
                      content={description}
                    />
                    <meta
                      key="twitter:image"
                      name="twitter:image"
                      content={image}
                    />
                    <meta
                      key="application-name"
                      name="application-name"
                      content="Utoniq"
                    ></meta>
                    <title>{title}</title>
                  </Head>
                  <SplitFactory
                    config={{
                      core: {
                        key: user?.uid || analytics.user('anonymousId'),
                        authorizationKey: splitioAuthorizeKey,
                      },
                    }}
                  >
                    <Component {...pageProps} />
                  </SplitFactory>
                </MuiThemeProvider>
              </StyledThemeProvider>
            </MuiPickersUtilsProvider>
          </AnalyticsProvider>
        </MessageProvider>
      </UserProvider>
    </ErrorBoundary>
  )
}

const ComposedApp = withApollo({ ssr: true })(appWithTranslation(UtoniqApp))

/**
 * Custom Appを利用する際に記載が必要
 * @see https://github.com/zeit/next.js/blob/master/errors/opt-out-automatic-prerendering.md
 * SSRを実施するために、 UtoniqAppに直接指定しなければならない。
 *
 */
UtoniqApp.getInitialProps = async ({ Component, ctx }: AppContext) => {
  const artistName = getStandaloneName(ctx.req)

  let pageProps = {}
  try {
    // @ts-ignore
    const user = ctx.req ? ctx.req?.session.user : await getUser()
    identify(user)

    if (Component.getInitialProps)
      pageProps = await Component.getInitialProps(ctx)

    return { pageProps, user, artistName }
  } catch (error) {
    const errorEventId = captureException(error, ctx)

    return {
      hasError: true,
      errorEventId,
      pageProps,
      artistName,
    }
  }
}

export default ComposedApp

function identify(user) {
  if (!user) return
  analytics.identify(user.uid, {
    avatar: user?.photoURL,
    email: user?.email,
    name: user?.displayName,
    created_at: user?.metadata?.creationTime,
    artist: user?.customClaims?.artist,
  })
  Sentry.setUser({
    id: user.uid,
    email: user.email,
    name: user.displayName,
  })
}
